import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Drawer,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import MenuIcon from '@mui/icons-material/Menu';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import Sidebar from '../components/Sidebar';
import { 
  getBonusEligibility, 
  clearBonusCycle, 
  issueBonus 
} from '../api/services';

// Shared look for the Finance pages (design tokens + small UI helpers)
const fin = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  warning: '#b26a00',
  warningSoft: '#fff4e5',
  radius: '16px',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const primaryButtonSx = {
  backgroundColor: fin.primary,
  color: '#fff',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': { backgroundColor: fin.primaryDark, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
};

const headCellSx = {
  fontWeight: 700,
  color: fin.primaryDark,
  backgroundColor: fin.primarySoft,
  fontSize: '0.8rem',
  py: 1.5,
  borderBottom: `1px solid ${fin.border}`,
};

const bodyCellSx = {
  color: fin.text,
  py: 1.75,
  fontSize: '0.875rem',
  borderBottom: `1px solid ${fin.border}`,
};

const mobileCardSx = {
  border: `1px solid ${fin.border}`,
  borderRadius: '12px',
  p: 2,
  backgroundColor: '#fff',
};

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
};

const FinancePageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        color: fin.text,
        letterSpacing: 0.5,
        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: fin.primary,
          height: '4px',
          width: { xs: '80px', sm: '100px' },
          position: 'absolute',
          left: 0,
          bottom: '-8px',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const FinanceMenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: 'block', md: 'none' },
      position: 'sticky',
      top: { xs: '64px', sm: '80px' },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: 'linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
          color: '#fff',
          borderRadius: '12px',
          p: 1.2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            background: 'linear-gradient(135deg, #007a8a 0%, #005b66 100%)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: '1.6rem' }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: fin.radius,
      border: `1px solid ${fin.border}`,
      backgroundColor: '#fff',
      boxShadow: fin.shadow,
      overflow: 'visible',
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: fin.text,
            pl: 1.5,
            borderLeft: `4px solid ${fin.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const chipTones = {
  success: { bg: fin.successSoft, color: fin.success },
  danger: { bg: fin.dangerSoft, color: fin.danger },
  warning: { bg: fin.warningSoft, color: fin.warning },
  info: { bg: fin.primarySoft, color: fin.primaryDark },
  neutral: { bg: '#eef1f4', color: '#4b5563' },
};

const PillChip = ({ label, tone = 'neutral' }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: '8px',
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message, action }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: fin.primarySoft,
        color: fin.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: fin.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: fin.muted, mt: 0.5, maxWidth: 360, mx: 'auto' }}
      >
        {message}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);


// Amount issued to each eligible employee when the cycle is cleared
const BONUS_AMOUNT = 1000;

const BonusManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [totalElements, setTotalElements] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchBonusEligibility = async (pageNum = page, size = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBonusEligibility(pageNum, size);
      
      let employeeData = [];
      let total = 0;
      
      if (response && response.data) {
        if (response.data.content && Array.isArray(response.data.content)) {
          employeeData = response.data.content;
          total = response.data.totalElements || employeeData.length;
        }
        else if (Array.isArray(response.data)) {
          employeeData = response.data;
          total = response.data.length;
        }
      }
      
      setEmployees(employeeData);
      setFilteredEmployees(employeeData);
      setTotalElements(total);
    } catch (err) {
      setError('Failed to fetch employee bonus eligibility. Please try again.');
      console.error('Error fetching bonus eligibility:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonusEligibility(page, rowsPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchBonusEligibility(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchBonusEligibility(0, newSize);
  };

  const handleClearCycle = async () => {
    setLoading(true);
    setError(null);
    try {
      await clearBonusCycle();
      
      if (Array.isArray(filteredEmployees)) {
        const eligibleEmployees = filteredEmployees.filter(emp => 
          emp && emp.bonusEligibilityStatus === 'Eligible'
        );
        
        for (const employee of eligibleEmployees) {
          try {
            await issueBonus({
              employeeId: employee.employeeId,
              bonusAmount: BONUS_AMOUNT
            });
          } catch (bonusError) {
            console.error(`Failed to issue bonus for employee ${employee.employeeId}:`, bonusError);
          }
        }
      }
      
      setSuccessMessage('Bonus cycle cleared and bonuses issued successfully!');
      await fetchBonusEligibility(0, rowsPerPage);
      
    } catch (err) {
      setError('Failed to clear bonus cycle. Please try again.');
      console.error('Error clearing bonus cycle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError(null);
  };

  // UI-only: confirm before running the existing handleClearCycle
  const handleOpenConfirm = () => setConfirmOpen(true);
  const handleCloseConfirm = () => setConfirmOpen(false);
  const handleConfirmClearCycle = () => {
    setConfirmOpen(false);
    handleClearCycle();
  };

  // Summary figures for the employees currently listed
  const listedEmployees = Array.isArray(filteredEmployees)
    ? filteredEmployees.filter(Boolean)
    : [];
  const eligibleCount = listedEmployees.filter(
    (emp) => emp.bonusEligibilityStatus === 'Eligible'
  ).length;

  const clearDisabled =
    !Array.isArray(employees) || employees.length === 0 || loading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Snackbar 
        open={!!successMessage || !!error} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? 'error' : 'success'} 
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {error || successMessage}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: '50px', sm: '60px', md: '120px' },
            backgroundColor: '#fff',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          <FinanceMenuButton onClick={handleDrawerToggle} />

          <FinancePageHeader
            title="Bonus Management"
          />

          <SectionCard
            title="Employee bonus eligibility"
            action={
              <PillChip
                tone="info"
                label={`${totalElements.toLocaleString()} employee${
                  totalElements !== 1 ? 's' : ''
                }`}
              />
            }
          >
            {loading ? (
              <ListSkeleton rows={rowsPerPage > 5 ? 6 : rowsPerPage} />
            ) : !Array.isArray(filteredEmployees) || filteredEmployees.length === 0 ? (
              <EmptyState
                icon={EmojiEventsOutlinedIcon}
                title="No employee data available"
                message="There is nothing to show for the current bonus cycle yet. Refresh to check again."
                action={
                  <Button
                    onClick={() => fetchBonusEligibility(page, rowsPerPage)}
                    startIcon={<RefreshIcon />}
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                      color: fin.primary,
                      borderColor: fin.primary,
                      '&:hover': {
                        borderColor: fin.primaryDark,
                        backgroundColor: fin.primarySoft,
                      },
                    }}
                  >
                    Refresh
                  </Button>
                }
              />
            ) : (
              <>
                {/* Desktop table */}
                <Box
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    overflowX: 'auto',
                    border: `1px solid ${fin.border}`,
                    borderRadius: '12px',
                  }}
                >
                  <Table sx={{ minWidth: 560 }}>
                    <TableHead>
                      <TableRow>
                        {['Name', 'Job title', 'Bonus eligibility', 'Validated points'].map((header) => (
                          <TableCell
                            key={header}
                            align={header === 'Validated points' ? 'right' : 'left'}
                            sx={headCellSx}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEmployees.map((employee, index) => {
                        if (!employee) return null;
                        
                        const isEligible = employee.bonusEligibilityStatus === 'Eligible';
                        
                        return (
                          <TableRow
                            key={employee.employeeId || index}
                            hover
                            sx={{ '&:last-child td': { borderBottom: 0 } }}
                          >
                            <TableCell sx={bodyCellSx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    backgroundColor: fin.primarySoft,
                                    color: fin.primaryDark,
                                  }}
                                >
                                  {getInitials(employee.employeeName)}
                                </Avatar>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                  {employee.employeeName || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {employee.jobTitle || 'N/A'}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <PillChip
                                label={employee.bonusEligibilityStatus || 'Unknown'}
                                tone={isEligible ? 'success' : 'danger'}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 600 }}>
                              {employee.totalValidatedPoints?.toFixed(2) || '0.00'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>

                {/* Mobile / tablet cards */}
                <Box
                  sx={{
                    display: { xs: 'flex', lg: 'none' },
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {filteredEmployees.map((employee, index) => {
                    if (!employee) return null;

                    const isEligible = employee.bonusEligibilityStatus === 'Eligible';

                    return (
                      <Box key={employee.employeeId || index} sx={mobileCardSx}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                backgroundColor: fin.primarySoft,
                                color: fin.primaryDark,
                              }}
                            >
                              {getInitials(employee.employeeName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700, color: fin.text }} noWrap>
                                {employee.employeeName || 'N/A'}
                              </Typography>
                              <Typography variant="body2" sx={{ color: fin.muted }} noWrap>
                                {employee.jobTitle || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <PillChip
                            label={employee.bonusEligibilityStatus || 'Unknown'}
                            tone={isEligible ? 'success' : 'danger'}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 1.5,
                            p: 1.5,
                            borderRadius: '10px',
                            backgroundColor: fin.surface,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: fin.muted }}>
                            Validated points
                          </Typography>
                          <Typography sx={{ fontWeight: 700 }}>
                            {employee.totalValidatedPoints?.toFixed(2) || '0.00'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                mt: 2,
                color: fin.text,
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  px: 0,
                },
                '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
              }}
              labelRowsPerPage="Rows per page:"
              nextIconButtonProps={{ "aria-label": "Next page" }}
              backIconButtonProps={{ "aria-label": "Previous page" }}
              showFirstButton
              showLastButton
            />

            {/* Cycle action */}
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: `1px solid ${fin.border}`,
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'center' },
              }}
            >
              <Button
                variant="contained"
                onClick={handleOpenConfirm}
                disabled={clearDisabled}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} sx={{ color: 'inherit' }} />
                  ) : (
                    <EmojiEventsOutlinedIcon />
                  )
                }
                sx={{ ...primaryButtonSx, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
              >
                {loading ? 'Working...' : 'Clear cycle & issue bonuses'}
              </Button>
            </Box>
          </SectionCard>
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      {/* Confirmation before clearing the cycle */}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: { xs: 2, sm: 4 } } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontWeight: 700,
            color: fin.text,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: fin.warningSoft,
              color: fin.warning,
            }}
          >
            <WarningAmberRoundedIcon fontSize="small" />
          </Box>
          Clear cycle & issue bonuses?
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ color: fin.text }}>
            This clears the current bonus cycle and issues KSh{' '}
            {BONUS_AMOUNT.toLocaleString()} to each of the{' '}
            <strong>{eligibleCount}</strong> eligible employee
            {eligibleCount !== 1 ? 's' : ''} listed on this page. This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseConfirm}
            sx={{ color: fin.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClearCycle}
            sx={{ ...primaryButtonSx, py: 1 }}
          >
            Clear cycle & issue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BonusManagement;